import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, MapPin, ArrowRight, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const STAGE_ORDER = [
  "reperee", "a_analyser", "a_contacter", "contact_fait",
  "rdv_obtenu", "en_suivi", "convertie", "abandonnee",
];

// Semantic tones per stage
const STAGE_TONE = {
  reperee:      { bg: "#F1F5F9", fg: "#0B2545" },
  a_analyser:   { bg: "#FBF6EA", fg: "#7A5B14" },
  a_contacter:  { bg: "#FEF3C7", fg: "#78350F" },
  contact_fait: { bg: "#DBEAFE", fg: "#1E3A8A" },
  rdv_obtenu:   { bg: "#DCFCE7", fg: "#166534" },
  en_suivi:     { bg: "#E9D5FF", fg: "#581C87" },
  convertie:    { bg: "#059669", fg: "#F0FDF4" },
  abandonnee:   { bg: "#FEE2E2", fg: "#7F1D1D" },
};

function OppCard({ op, onAdvance }) {
  const currentIdx = STAGE_ORDER.indexOf(op.pipeline_stage);
  const next = STAGE_ORDER[currentIdx + 1];
  return (
    <div className="bg-white border rounded-lg p-3 mb-2 shadow-sm hover:shadow-md transition"
          style={{ borderColor: "#E5E7EB" }}
          data-testid={`abi-pipeline-card-${op.opportunity_id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest opacity-60">
          {op.opportunity_type}
        </div>
        <div className="text-xs font-bold" style={{ color: "#7A5B14" }}>
          {op.priority_score}
        </div>
      </div>
      <div className="font-serif font-semibold text-sm leading-tight mb-1"
            style={{ color: "#0B2545" }}>
        {op.title_fr}
      </div>
      {op.territory?.city && (
        <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
          <MapPin size={10} /> {op.territory.city}
        </div>
      )}
      {next && next !== "abandonnee" && (
        <button
          type="button"
          onClick={() => onAdvance(op, next)}
          className="mt-2 w-full text-[11px] font-semibold py-1 px-2 rounded border hover:bg-slate-50 inline-flex items-center justify-center gap-1"
          style={{ borderColor: "#E5E7EB", color: "#0B2545" }}
          data-testid={`abi-pipeline-card-${op.opportunity_id}-advance`}
        >
          Faire avancer <ChevronRight size={11} />
        </button>
      )}
    </div>
  );
}

export default function AbiPipeline() {
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/abi/radar/pipeline");
      setColumns(r.data?.columns || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error("Impossible de charger le pipeline.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const advance = async (op, newStage) => {
    try {
      await api.post(`/abi/radar/opportunities/${op.opportunity_id}/pipeline`,
                      { pipeline_stage: newStage });
      toast.success(`Avancé vers ${newStage}`);
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div data-testid="abi-pipeline-root">
      <TopBar
        title="Mon pipeline"
        subtitle="8 étapes du repérage à la conversion. Faites avancer vos opportunités."
      />

      <div className="px-6 md:px-10 py-8 md:py-10 max-w-[1600px] mx-auto space-y-6">

        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl font-bold" style={{ color: "#0B2545" }}>
            {total} opportunité{total > 1 ? "s" : ""} en pipeline
          </h2>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-lg border hover:bg-slate-50 disabled:opacity-50"
            style={{ borderColor: "#E5E7EB" }}
            data-testid="abi-pipeline-refresh"
          >
            {loading ? <Loader2 size={12} className="animate-spin inline" />
                     : <RefreshCcw size={12} className="inline mr-1" />}
            Actualiser
          </button>
        </div>

        {loading && columns.length === 0 ? (
          <div className="p-8 flex items-center gap-2 text-slate-600">
            <Loader2 size={16} className="animate-spin" /> Chargement…
          </div>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="grid gap-3"
                  style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(220px, 1fr))` }}
                  data-testid="abi-pipeline-columns">
              {columns.map((c) => {
                const tone = STAGE_TONE[c.key] || STAGE_TONE.reperee;
                return (
                  <div key={c.key} className="rounded-xl p-3"
                        style={{ background: "#F9FAFB", minHeight: 200 }}
                        data-testid={`abi-pipeline-column-${c.key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded"
                             style={{ background: tone.bg, color: tone.fg }}>
                        {c.label_fr}
                      </span>
                      <span className="text-xs font-bold text-slate-600">
                        {c.count}
                      </span>
                    </div>
                    <div>
                      {(c.items || []).map((op) => (
                        <OppCard key={op.opportunity_id} op={op} onAdvance={advance} />
                      ))}
                      {(c.items || []).length === 0 && (
                        <div className="text-[11px] text-slate-400 italic text-center py-4">
                          Vide
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
